/**
 * OS Comments TanStack Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from '../api';

// =============================================================================
// Helpers
// =============================================================================

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Keys
// =============================================================================

export const commentKeys = {
  all: ['os-comments'] as const,
  list: (entityType: string, entityId: string) =>
    [...commentKeys.all, 'list', entityType, entityId] as const,
};

// =============================================================================
// Hooks
// =============================================================================

export function useComments(entityType: string, entityId: string) {
  return useQuery({
    queryKey: commentKeys.list(entityType, entityId),
    queryFn: () => api.getComments(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });
}

export function useCreateComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { entity_type: string; entity_id: string; body: string }) =>
      api.createComment(data),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({
        queryKey: commentKeys.list(variables.entity_type, variables.entity_id),
      });
      toast.success('Comment added');
    },
    onError: (e) => toast.error('Failed to add comment', { description: getErrorMessage(e) }),
  });
}
